import type { KeyboardEvent } from "react";

/**
 * Keyboard behaviour for a tablist, per the WAI-ARIA pattern and matching the
 * approved design's own handler: arrows wrap, Home and End jump to the ends, and
 * activation follows focus.
 *
 * Automatic activation is correct here because every panel is static markup that
 * is already in memory — there is nothing to fetch and no cost to arrowing
 * through. Roving tabIndex is the caller's job: exactly one tab carries 0.
 */
export function useRovingTablist<T extends string>(
  items: readonly T[],
  current: T,
  onSelect: (next: T) => void,
  orientation: "horizontal" | "vertical" = "vertical"
) {
  const nextKey = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
  const prevKey = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";

  return (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = items.indexOf(current);
    let target: number | undefined;

    if (event.key === nextKey) {
      target = (index + 1) % items.length;
    } else if (event.key === prevKey) {
      target = (index + items.length - 1) % items.length;
    } else if (event.key === "Home") {
      target = 0;
    } else if (event.key === "End") {
      target = items.length - 1;
    }

    if (target === undefined) {
      return;
    }

    event.preventDefault();
    const key = items.at(target);
    if (key === undefined) {
      return;
    }
    onSelect(key);
    // Focus has to follow selection or the roving tabIndex strands the user on a
    // tab that is no longer tabbable.
    document.getElementById(`tab-${key}`)?.focus();
  };
}
